/* @flow */
import * as React from "react";
import debounce from "lodash/debounce";
import prefixStyles from "./utils/prefixStyles";
import BackButtonResponder from "./BackButtonResponder";
import MountOnBody from "./MountOnBody";
import Theme from "./Theme";
import FocusController from "./FocusController";

const ARROW_SIZE = 6;

const baseStyles = {
  root: {
    display: "flex",
    position: "fixed",
    top: "0",
    right: "0",
    bottom: "0",
    left: "0",
    zIndex: Theme.zIndexLayers.POPUP,
  },
  childWrapperPrefixed: prefixStyles({
    flexShrink: "0",
    flexGrow: "0",
  }),
  childWrapper: ({ open }) => ({
    ...baseStyles.childWrapperPrefixed,
    position: "relative",
    transition: open
      ? "transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
      : "transform 0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",
    transform: open ? "scale(1.0)" : "scale(0.0)",
  }),
  arrow: {
    position: "absolute",
    width: 0,
    height: 0,
    border: `solid ${ARROW_SIZE}px transparent`,
    zIndex: "1", // render above potential box shadow of childWrapper children
  },
};

const styles = {
  // $FlowFixMe spread operator is buggy https://github.com/facebook/flow/issues/3061
  root: prefixStyles({
    topLeft: {
      ...baseStyles.root,
      justifyContent: "flex-start",
      alignItems: "flex-start",
    },
    topRight: {
      ...baseStyles.root,
      justifyContent: "flex-end",
      alignItems: "flex-start",
    },
    bottomLeft: {
      ...baseStyles.root,
      justifyContent: "flex-start",
      alignItems: "flex-end",
    },
    bottomRight: {
      ...baseStyles.root,
      justifyContent: "flex-end",
      alignItems: "flex-end",
    },
  }),
  childWrapper: {
    topLeft: ({ targetRect, open }) => ({
      ...baseStyles.childWrapper({ open }),
      transformOrigin: `${targetRect.width / 2}px ${-ARROW_SIZE}px`,
      marginTop: targetRect.bottom + ARROW_SIZE,
      marginLeft: targetRect.left,
    }),
    topRight: ({ targetRect, open }) => ({
      ...baseStyles.childWrapper({ open }),
      transformOrigin: `calc(100% - ${targetRect.width /
        2}px) ${-ARROW_SIZE}px`,
      marginTop: targetRect.bottom + ARROW_SIZE,
      marginRight: window.innerWidth - targetRect.right,
    }),
    bottomLeft: ({ targetRect, open }) => ({
      ...baseStyles.childWrapper({ open }),
      transformOrigin: `${targetRect.width / 2}px calc(100% + ${ARROW_SIZE}px)`,
      marginBottom: targetRect.top + targetRect.height + ARROW_SIZE,
      marginLeft: targetRect.left,
    }),
    bottomRight: ({ targetRect, open }) => ({
      ...baseStyles.childWrapper({ open }),
      transformOrigin: `calc(100% - ${targetRect.width /
        2}px) calc(100% + ${ARROW_SIZE}px)`,
      marginBottom: targetRect.bottom + ARROW_SIZE,
      marginRight: targetRect.left,
    }),
  },
  arrow: {
    topLeft: ({ arrowBackgroundColor, targetRect }) => ({
      ...baseStyles.arrow,
      borderBottomColor: arrowBackgroundColor,
      transform: "translateY(-100%)",
      top: 0,
      left: targetRect.width / 2 - ARROW_SIZE,
    }),
    topRight: ({ arrowBackgroundColor, targetRect }) => ({
      ...baseStyles.arrow,
      borderBottomColor: arrowBackgroundColor,
      transform: "translateY(-100%)",
      top: 0,
      right: targetRect.width / 2 - ARROW_SIZE,
    }),
    bottomLeft: ({ arrowBackgroundColor, targetRect }) => ({
      ...baseStyles.arrow,
      borderTopColor: arrowBackgroundColor,
      transform: "translateY(100%)",
      bottom: 0,
      left: targetRect.width / 2 - ARROW_SIZE,
    }),
    bottomRight: ({ arrowBackgroundColor, targetRect }) => ({
      ...baseStyles.arrow,
      borderTopColor: arrowBackgroundColor,
      transform: "translateY(100%)",
      bottom: 0,
      right: targetRect.width / 2 - ARROW_SIZE,
    }),
  },
};

type Props = {
  open: boolean,
  onRequestClose: () => void,
  children: React.Node,
  arrowPosition: "topLeft" | "topRight" | "bottomLeft" | "bottomRight",
  getTargetRect: () => ?ClientRect,
  arrowBackgroundColor: string,
};

type State = {
  targetRect: ?ClientRect,
  closing: boolean,
};

export default class Popover extends React.Component<Props, State> {
  static defaultProps = {
    arrowBackgroundColor: "white",
  };

  state = {
    targetRect: null,
    closing: false,
  };

  _isMounted = false;
  _wrapper: ?HTMLElement;

  componentDidMount() {
    this._isMounted = true;
    this._updateTargetRect();

    window.addEventListener("resize", this._debouncedUpdateTargetRect);
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener("resize", this._debouncedUpdateTargetRect);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this._updateTargetRect();
    if (this.props.open && !nextProps.open) {
      this.setState({ closing: true }, () => {
        setTimeout(() => {
          if (!this._isMounted) {
            return;
          }

          this.setState({ closing: false });
        }, 200);
      });
    }
  }

  _updateTargetRect = () => {
    if (!this._isMounted) {
      return;
    }

    const { getTargetRect } = this.props;
    this.setState({ targetRect: getTargetRect() });
  };

  _debouncedUpdateTargetRect = debounce(
    this._updateTargetRect,
    Theme.RESPONSIVE_REFRESH_RATE
  );

  _handleRootClicked = (event: SyntheticMouseEvent<HTMLElement>) => {
    if (!this._wrapper) {
      return;
    }

    if (
      !(event.target instanceof Node) ||
      !this._wrapper.contains(event.target)
    ) {
      this.props.onRequestClose();
    }
  };

  _handleBackButtonPressed = () => {
    this.props.onRequestClose();
    return { handled: true };
  };

  render() {
    const { open, children, arrowPosition, arrowBackgroundColor } = this.props;
    const { targetRect, closing } = this.state;

    if (targetRect == null) {
      // Usually, the parent is not able to provide a target rect from a DOM
      // node in until componentDidMount (once it has the ref), so the first
      // time we render, we don't have a target rect.
      return null;
    }

    return (
      <MountOnBody>
        <FocusController
          focused={open}
          onFocusExited={this.props.onRequestClose}
        >
          {({ captureTargetRef }) => (
            <div
              style={{
                ...styles.root[arrowPosition],
                ...(open
                  ? null
                  : {
                      pointerEvents: "none",
                    }),
              }}
              // eslint-disable-next-line nexia/prefer-tappable
              onClick={this._handleRootClicked}
            >
              {open ? (
                <BackButtonResponder
                  onPressed={this._handleBackButtonPressed}
                />
              ) : null}
              <div
                ref={(el) => (this._wrapper = el)}
                style={styles.childWrapper[arrowPosition]({ targetRect, open })}
                data-component-name="Popover"
                data-component-prop-open={String(Boolean(open))}
              >
                <div
                  style={styles.arrow[arrowPosition]({
                    arrowBackgroundColor,
                    targetRect,
                  })}
                />
                {open || closing ? (
                  <div ref={captureTargetRef} tabIndex={-1}>
                    {children}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </FocusController>
      </MountOnBody>
    );
  }
}



// WEBPACK FOOTER //
// ../nx-components/src/Popover.jsx